import { API_URL } from "@/shared/config";

export const WS_APP_PATH = "/ws/app/";

/**
 * Joint l'origine WS et le chemin sans dupliquer le segment `/ws`.
 * Ex. `wss://host/ws` + `/ws/app/` → `wss://host/ws/app/`
 */
function joinWsOriginAndPath(origin: string, path: string): string {
  const base = origin.endsWith("/") ? origin.slice(0, -1) : origin;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (base.endsWith("/ws") && normalizedPath.startsWith("/ws/")) {
    return `${base}${normalizedPath.slice(3)}`;
  }
  return `${base}${normalizedPath}`;
}

export const WS_EVENTS = {
  // Infrastructure
  PING: "ping",
  PONG: "pong",
  ERROR: "error",
  CONNECTION_INIT: "connectionInit",
  CONNECTION_DISCONNECT: "connectionDisconnect",

  // Messaging
  MESSAGING_MESSAGE_SENT: "messaging.messageSent",
  MESSAGING_MESSAGE_RECEIVE: "messaging.messageReceive",
  MESSAGING_MESSAGE_READ: "messaging.messageRead",
  MESSAGING_CONVERSATION_STATUS: "messaging.conversationStatus",
  MESSAGING_CONVERSATION_CLOSED: "messaging.conversationClosed",

  // Ticketing
  TICKETING_TICKET_CREATED: "ticketing.ticketCreated",
  TICKETING_TICKET_UPDATED: "ticketing.ticketUpdated",
  TICKETING_TICKET_ASSIGNED: "ticketing.ticketAssigned",
  TICKETING_TICKET_MERGED: "ticketing.ticketMerged",
  TICKETING_TICKET_RESOLVED: "ticketing.ticketResolved",
  TICKETING_TICKET_DELETED: "ticketing.ticketDeleted",

  // Cart (conciergerie)
  CART_ORDER_CREATED: "cart.orderCreated",
  CART_ORDER_STATUS_CHANGED: "cart.orderStatusChanged",

  // CartBar (bar)
  CART_BAR_ORDER_CREATED: "cartBar.orderCreated",
  CART_BAR_ORDER_STATUS_CHANGED: "cartBar.orderStatusChanged",

  // Colis
  COLIS_PARCEL_RECEIVED: "colis.parcelReceived",
  COLIS_PARCEL_CREATED: "colis.parcelCreated",
  COLIS_PARCEL_READY: "colis.parcelReady",
  COLIS_PARCEL_COLLECTED: "colis.parcelCollected",
  COLIS_PARCEL_DELETED: "colis.parcelDeleted",

  // Services
  SERVICES_RESERVATION_CREATED: "services.reservationCreated",
  SERVICES_RESERVATION_CANCELLED: "services.reservationCancelled",
  SERVICES_RESERVATION_UPDATED: "services.reservationUpdated",
  SERVICES_RESERVATION_REMINDER: "services.reservationReminder",
  SERVICES_SLOT_CHANGED: "services.slotChanged",

  // List
  LIST_APPOINTMENT_ARRIVED: "list.appointmentArrived",
  LIST_APPOINTMENT_DELETED: "list.appointmentDeleted",
  LIST_UPDATED: "list.listUpdated",

  // Salle (réunions)
  SALLE_RESERVATION_CREATED: "salle.reservationCreated",
  SALLE_RESERVATION_UPDATED: "salle.reservationUpdated",
  SALLE_RESERVATION_DELETED: "salle.reservationDeleted",
} as const;

export type WsEventName = (typeof WS_EVENTS)[keyof typeof WS_EVENTS];

export interface WsEnvelope<T = Record<string, unknown>> {
  event: WsEventName | string;
  payload: T;
}

function resolveWsOrigin(): string {
  const explicit = (import.meta.env.VITE_WS_URL as string | undefined)?.replace(/\/$/, "");
  if (explicit) return explicit;

  if (import.meta.env.DEV && typeof window !== "undefined") {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}`;
  }

  return API_URL.replace(/^http/, "ws").replace(":8000", ":8001").replace(/\/$/, "");
}

export const WS_ORIGIN = resolveWsOrigin();

export function buildWsUrl(token: string): string {
  const params = new URLSearchParams({ token });
  return `${joinWsOriginAndPath(WS_ORIGIN, WS_APP_PATH)}?${params.toString()}`;
}
